<template>
  <div class="fee-summary-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeSummary.page.title')" :subtitle="$t('feeSummary.page.subtitle')">
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listFeeSummarys(pagination.current, pagination.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('feeSummary.title') }}</span>
        </template>
        <template #right>
          <el-button-group class="ds-summary-switch">
            <el-button size="mini" :type="feeSummaryInfo.feeSummaryType === 1001 ? 'primary' : ''"
              @click="_switchFeeSummaryType(1001)">
              {{ $t('feeSummary.day') }}
            </el-button>
            <el-button size="mini" :type="feeSummaryInfo.feeSummaryType === 1101 ? 'primary' : ''"
              @click="_switchFeeSummaryType(1101)">
              {{ $t('feeSummary.month') }}
            </el-button>
            <el-button size="mini" :type="feeSummaryInfo.feeSummaryType === 1111 ? 'primary' : ''"
              @click="_switchFeeSummaryType(1111)">
              {{ $t('feeSummary.year') }}
            </el-button>
          </el-button-group>
        </template>
      </ds-toolbar>

      <ds-data-table :data="feeSummaryInfo.fees" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('feeSummary.page.empty')"
        @retry="_listFeeSummarys(pagination.current, pagination.size)">
        <el-table-column prop="createTime" :label="$t('feeSummary.time')" align="center" width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="configId" :label="$t('feeSummary.feeItemId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.configId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('feeSummary.feeItemName')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="receivableAmounts" :label="$t('feeSummary.receivableAmount')" align="right" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.receivableAmounts) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="receivedAmounts" :label="$t('feeSummary.receivedAmount')" align="right" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.receivedAmounts) }}</span>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('feeSummary.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>
  </div>
</template>

<script>
import { listFeeSummarys } from '@/api/fee/feeSummaryApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeSummaryList',
  components: {
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeSummaryInfo: {
        fees: [],
        feeSummaryType: '1001',
        total: 0
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._listFeeSummarys(this.pagination.current, this.pagination.size)
  },
  methods: {
    async _listFeeSummarys(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page,
          row: size,
          feeSummaryType: this.feeSummaryInfo.feeSummaryType,
          communityId: this.communityId
        }
        const { fees, total } = await listFeeSummarys(params)
        this.feeSummaryInfo.fees = Array.isArray(fees) ? fees : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取缴费汇总数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('feeSummary.page.loadFailed')
        this.$message.error(this.$t('feeSummary.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _switchFeeSummaryType(feeSummaryType) {
      this.feeSummaryInfo.feeSummaryType = feeSummaryType
      this.pagination.current = 1
      this._listFeeSummarys(this.pagination.current, this.pagination.size)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listFeeSummarys(this.pagination.current, this.pagination.size)
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listFeeSummarys(this.pagination.current, this.pagination.size)
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-summary-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 汇总维度切换按钮组 */
.ds-summary-switch {
  margin-left: var(--ds-spacing-sm);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
